import * as React from 'react';
import { useCallback } from 'react';
import { Collapse } from '@mui/material';
import cn from 'classnames';

import { useTheme } from '../../../../../theme';
import { ThemeProvider } from '../../../../../theme';
import { IconButton } from '../../../../../BaseComponents';
import { FareBreakdown, FareBreakdownProps } from '../FareBreakdown';
import { CdnIcon } from '@shared/ui';

interface Props {
	className?: string;
	classNames?: {
		root?: string;
		trigger?: string;
	};
	defaultOpen?: boolean;
	breakdown: FareBreakdownProps['breakdown'];
	dataTestId?: string;
	dataState?: string;
}

const TaxesCollapse: React.FC<Props> = ({
	className,
	classNames,
	defaultOpen = false,
	breakdown,
	dataTestId,
	dataState,
	children
}) => {
	const { TaxesCollapse: theme } = useTheme('Entities').Fare;

	const [open, setOpen] = React.useState(defaultOpen);

	const content = breakdown.total && breakdown.groups.length > 0 && (
		<ThemeProvider
			value={{
				Entities: {
					Fare: {
						FareBreakdown: {
							price__label: theme.price__label,
							price__item: theme.price__item,
							price__item_highlighted: theme.price__item_highlighted
						}
					}
				}
			}}
		>
			<FareBreakdown breakdown={breakdown} />
		</ThemeProvider>
	);

	const isEnabled = !!content;

	const handleToggle = useCallback(() => {
		if (!isEnabled) {
			return;
		}

		setOpen(open => !open);
	}, [isEnabled, setOpen]);

	const handleKeyDown: React.KeyboardEventHandler = useCallback(
		e => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				handleToggle();
			}
		},
		[handleToggle]
	);

	return (
		<div
			className={cn(theme.root, className, classNames?.root, {
				[theme.enabled]: isEnabled,
				[theme.rootOpened]: open
			})}
		>
			<div
				className={cn(theme.trigger, classNames?.trigger)}
				role="button"
				tabIndex={0}
				aria-expanded={open}
				data-test-id={dataTestId}
				data-state={dataState}
				onClick={handleToggle}
				onKeyDown={handleKeyDown}
			>
				<div className={theme.triggerContent}>{children}</div>
				{isEnabled && (
					<IconButton className={theme.triggerButton}>
						<CdnIcon icon="Arrow/Expand_more" />
					</IconButton>
				)}
			</div>
			{isEnabled && (
				<Collapse in={open} timeout="auto" unmountOnExit>
					{content}
				</Collapse>
			)}
		</div>
	);
};

export default TaxesCollapse;
